import { Stack } from '@mui/material';
import { type ReactNode } from 'react';
import { VyTypography } from '../../data-display/VyTypography';

/**
 * Internal helper used by all `Vy*Field` input primitives. Renders the
 * label as a separate Typography above the field, then the field itself,
 * then optional helper text below. Matches the stacked-label form pattern
 * used across the contract / plan editor screens — labels are not
 * floating-in-notch, they sit above the input.
 *
 * The wrapped field should not receive a `label` prop of its own — this
 * helper owns label positioning.
 */
export type LabeledFieldProps = {
  /** Field label rendered above the input. Omit to render a label-less field. */
  label?: string;
  /** Hint shown below the input when no error; error message when `error` is true. */
  helperText?: string;
  /** Tints helper text in the error color. */
  error?: boolean;
  /** Width override forwarded to the outer stack. Defaults to 100%. */
  width?: number | string;
  children: ReactNode;
};

export function LabeledField({ label, helperText, error, width = '100%', children }: LabeledFieldProps) {
  return (
    <Stack sx={{ gap: 0.5, width }}>
      {label && (
        <VyTypography variant="inputLabel" sx={{ color: 'text.primary' }}>
          {label}
        </VyTypography>
      )}
      {children}
      {helperText && (
        <VyTypography variant="fieldHelper" sx={error ? { color: 'error.main' } : undefined}>
          {helperText}
        </VyTypography>
      )}
    </Stack>
  );
}
